/**
 * GovDemoGuide — Painel flutuante com sugestões de perguntas para
 * usar durante APRESENTAÇÕES COMERCIAIS.
 *
 * Sprint 3 · v0.98 · Fev/2026 · 100% aditivo.
 *
 * Aparece SOMENTE quando `/api/demo/info.is_demo=true`. Cliente real
 * NUNCA vê. Bolha canto inferior-direito, expansível, com 32
 * perguntas curadas — 19 delas de "efeito uau" (situações reais que
 * impressionam durante demos com prefeitos e secretários).
 *
 * Objetivo: durante a demo, o vendedor abre o painel, copia a pergunta,
 * cola no simulador de WhatsApp — a IA responde puxando o documento
 * institucional correto.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const SECRETARIA_COLOR = {
  saude: "bg-rose-100 text-rose-800",
  fazenda: "bg-amber-100 text-amber-800",
  educacao: "bg-indigo-100 text-indigo-800",
  obras: "bg-orange-100 text-orange-800",
  assistencia_social: "bg-emerald-100 text-emerald-800",
  meio_ambiente: "bg-lime-100 text-lime-800",
  cultura_esporte: "bg-fuchsia-100 text-fuchsia-800",
  seguranca: "bg-slate-100 text-slate-800",
  administracao: "bg-sky-100 text-sky-800",
  recursos_humanos: "bg-teal-100 text-teal-800",
  ouvidoria: "bg-purple-100 text-purple-800",
  procuradoria: "bg-neutral-100 text-neutral-800",
};

const SECRETARIA_LABEL = {
  saude: "Saúde",
  fazenda: "Fazenda",
  educacao: "Educação",
  obras: "Obras",
  assistencia_social: "Assistência",
  meio_ambiente: "Meio Ambiente",
  cultura_esporte: "Cultura",
  seguranca: "Segurança",
  administracao: "Administração",
  recursos_humanos: "RH",
  ouvidoria: "Ouvidoria",
  procuradoria: "Procuradoria",
};

const QUESTIONS = [
  // BÁSICAS
  { q: "Como emitir a 2ª via do IPTU?", secretaria: "fazenda", wow: false },
  { q: "Como parcelar o IPTU 2026?", secretaria: "fazenda", wow: false },
  { q: "Como emitir ISS e nota fiscal de serviço?", secretaria: "fazenda", wow: false },
  { q: "Onde fica o CRAS mais próximo?", secretaria: "assistencia_social", wow: false },
  { q: "Como agendar vacinação?", secretaria: "saude", wow: false },
  { q: "Como solicitar poda de árvore?", secretaria: "obras", wow: false },
  { q: "Como pedir troca de lâmpada da iluminação pública?", secretaria: "obras", wow: false },
  { q: "Como solicitar tapa-buraco na minha rua?", secretaria: "obras", wow: false },
  { q: "Como funciona a coleta seletiva?", secretaria: "meio_ambiente", wow: false },
  { q: "Como conseguir vaga em creche?", secretaria: "educacao", wow: false },
  { q: "Como funciona o transporte escolar?", secretaria: "educacao", wow: false },
  { q: "Como denunciar descarte irregular de lixo?", secretaria: "meio_ambiente", wow: false },
  { q: "Como solicitar coleta de entulho?", secretaria: "meio_ambiente", wow: false },
  // EFEITO UAU
  { q: "Meu filho vai completar 4 anos em maio. Como faço para matriculá-lo na rede municipal em 2026?", secretaria: "educacao", wow: true },
  { q: "Sou idoso e aposentado, recebo 1,5 salário mínimo. Tenho direito à isenção do IPTU?", secretaria: "fazenda", wow: true },
  { q: "Quero abrir um pequeno comércio no bairro Jardim Aurora. Quais documentos e alvarás preciso?", secretaria: "fazenda", wow: true },
  { q: "A rua da minha casa está sem iluminação há três dias. Qual o prazo para resolverem?", secretaria: "obras", wow: true },
  { q: "Minha mãe é acamada e precisa de acompanhamento médico em casa. O município oferece esse serviço?", secretaria: "saude", wow: true },
  { q: "Quais eventos culturais vão acontecer em Santa Aurora neste fim de semana?", secretaria: "cultura_esporte", wow: true },
  { q: "Como participar do programa habitacional da prefeitura? Minha família tem renda de R$ 2 mil.", secretaria: "assistencia_social", wow: true },
  { q: "Fui vítima de violência doméstica na madrugada de ontem. Onde procuro ajuda imediata e sigilosa?", secretaria: "assistencia_social", wow: true },
  { q: "Preciso de um remédio para pressão alta que meu médico particular receitou. Consigo na farmácia municipal?", secretaria: "saude", wow: true },
  { q: "Meu filho tem TEA. Que apoio a rede municipal oferece na escola?", secretaria: "educacao", wow: true },
  { q: "Sou MEI e quero funcionar em casa. Isso é permitido no meu bairro?", secretaria: "fazenda", wow: true },
  { q: "Como impugnar uma multa de trânsito municipal?", secretaria: "seguranca", wow: true },
  { q: "Como faço para desmarcar minha consulta na UBS e onde vejo a data da próxima?", secretaria: "saude", wow: true },
  { q: "Minha família está sem renda este mês. Existe algum benefício municipal emergencial?", secretaria: "assistencia_social", wow: true },
  { q: "Trabalho perto de uma construção que joga entulho na rua todos os dias. Como denunciar?", secretaria: "meio_ambiente", wow: true },
  { q: "Onde consigo declaração de matrícula do meu filho para apresentar ao juiz?", secretaria: "educacao", wow: true },
  { q: "Meu vizinho está podando uma árvore na calçada por conta própria. Isso é permitido?", secretaria: "obras", wow: true },
  { q: "Como me candidatar ao concurso público que abriu na Secretaria de Saúde?", secretaria: "recursos_humanos", wow: true },
  { q: "Onde vejo os gastos da prefeitura com a merenda escolar deste ano?", secretaria: "administracao", wow: true },
];

export default function GovDemoGuide() {
  const [isDemo, setIsDemo] = useState(false);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("wow"); // wow | all | basic

  useEffect(() => {
    // Checa se o tenant é demo — só aparece se sim.
    api.get("/demo/info")
      .then((r) => setIsDemo(!!r.data?.is_demo))
      .catch(() => setIsDemo(false));
  }, []);

  if (!isDemo) return null;

  const copy = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success("Pergunta copiada");
    }).catch(() => toast.error("Não foi possível copiar"));
  };

  const filtered = QUESTIONS.filter((q) => {
    if (filter === "wow") return q.wow;
    if (filter === "basic") return !q.wow;
    return true;
  });

  return (
    <>
      {/* Botão flutuante */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="gov-demo-guide-toggle"
        className={`fixed bottom-5 right-5 z-40 rounded-full shadow-lg h-14 w-14 flex items-center justify-center transition-transform hover:scale-105 ${
          open ? "bg-slate-800 text-white" : "bg-emerald-600 text-white"
        }`}
        title={open ? "Fechar guia de demonstração" : "Abrir guia de demonstração"}
      >
        {open ? (
          <span className="text-xl">×</span>
        ) : (
          <span className="text-xl font-bold">?</span>
        )}
      </button>

      {open ? (
        <div
          data-testid="gov-demo-guide-panel"
          className="fixed bottom-24 right-5 z-40 w-[380px] max-w-[92vw] max-h-[70vh] bg-white rounded-xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
        >
          <div className="bg-slate-900 text-white p-4">
            <div className="text-[10.5px] uppercase tracking-[0.14em] text-emerald-300 font-semibold">
              Guia de Demonstração
            </div>
            <div className="mt-0.5 font-heading text-[15px] font-semibold">
              Perguntas para impressionar
            </div>
            <div className="mt-1 text-[11.5px] text-white/70 leading-snug">
              Clique para copiar e cole no simulador para ver a IA
              respondendo com base nos documentos oficiais do município.
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-slate-200 bg-slate-50">
            {[
              { code: "wow", label: `Efeito UAU (${QUESTIONS.filter((x) => x.wow).length})` },
              { code: "basic", label: `Básicas (${QUESTIONS.filter((x) => !x.wow).length})` },
              { code: "all", label: `Todas (${QUESTIONS.length})` },
            ].map((t) => (
              <button
                key={t.code}
                type="button"
                onClick={() => setFilter(t.code)}
                data-testid={`gov-demo-guide-tab-${t.code}`}
                className={`flex-1 py-2 text-[11.5px] font-medium transition-colors ${
                  filter === t.code
                    ? "bg-white text-emerald-700 border-b-2 border-emerald-600"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="gov-demo-guide-list">
            {filtered.map((q, i) => (
              <button
                key={i}
                type="button"
                onClick={() => copy(q.q)}
                data-testid={`gov-demo-guide-q-${i}`}
                className="w-full text-left rounded-lg border border-slate-200 bg-white p-2.5 hover:border-emerald-400 hover:bg-emerald-50/40 transition-colors"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="text-[12.5px] text-slate-800 leading-snug flex-1">
                    {q.q}
                  </div>
                  {q.wow ? (
                    <span className="text-[9px] px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded font-semibold uppercase tracking-wider">
                      UAU
                    </span>
                  ) : null}
                </div>
                <div className="mt-1.5 flex items-center gap-1.5">
                  <span
                    className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                      SECRETARIA_COLOR[q.secretaria] || "bg-slate-100 text-slate-700"
                    }`}
                  >
                    {SECRETARIA_LABEL[q.secretaria] || q.secretaria}
                  </span>
                  <span className="text-[10px] text-slate-400">Clique para copiar</span>
                </div>
              </button>
            ))}
          </div>

          <div className="border-t border-slate-100 p-2.5 bg-slate-50 text-center">
            <span className="text-[10.5px] text-slate-500">
              🎬 Ambiente de demonstração · isolado de clientes reais
            </span>
          </div>
        </div>
      ) : null}
    </>
  );
}
